import React, { useEffect, useMemo, useState, Component } from 'react';
/**
 * FeedbackDisplay — Two-phase feedback orchestrator (splash → detail).
 *
 * Phase 1 (splash): Full-screen celebration/encouragement via FeedbackSplash.
 * Phase 2 (detail): Answer comparison, question context, explanation, extra sections.
 *
 * The splash phase already communicates correct/wrong + XP, so the detail phase
 * focuses purely on learning: what was the question, what did you answer, and why.
 *
 * Co-located in practice/ domain (CL-1 s64).
 */
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import type { Question } from '../../../content';
import { Button } from '../../../components/ui/Button';
import { LatexRenderer } from '../../../components/ui/LatexRenderer';
import { resolveSupportContent } from '../../../support';
import { useDjaySelection, type DjayTier, type DjayVariant } from '../../../mascot';
// Sub-components
import { StatusBadge } from './StatusBadge';
import { AnswerComparison } from './AnswerComparison';
import { ExplanationCard } from './ExplanationCard';
import { ExplanationContent } from './ExplanationContent';
import { FeedbackAccordion } from './FeedbackAccordion';
import { FeedbackSplash } from './FeedbackSplash';
// Extracted utilities (s62 optimization: 691→~280 lines)
import { formatCorrectAnswer, formatUserAnswer, parseMoneyTokens } from './feedbackAnswerFormatters';
import { normalizeCents } from '../../../utils/moneyFormatters';
import { buildAccordionSections } from './feedbackSectionBuilder';
// ── Types ────────────────────────────────────────────────────
interface FeedbackDisplayProps {
  question: Question;
  wasCorrect: boolean;
  userAnswer: string;
  userAnswerIndex?: number;
  motivationalText: string;
  xpGained: number;
  onNext: () => void;
  isLastQuestion?: boolean;
  mode?: 'full' | 'compact';
  streak?: number;
  levelUp?: {
    level: number;
    xp: number;
    title: string;
  } | null;
  streakMilestone?: {
    count: number;
    message: string;
  } | null;
  dailyGoalReached?: boolean;
  tierEvolution?: {
    from: DjayTier;
    to: DjayTier;
  } | null;
  skinUnlock?: {
    skin: DjayVariant;
    description: string;
  } | null;
  onClearLevelUp?: () => void;
  /**
   * Active module's hex color. Threaded through to FeedbackSplash so the
   * celebration confetti can speak the current world's color (§16.E).
   */
  moduleColor?: string;
  familyMasteryPercent?: number;
  familyLabel?: string;
}
// ── Component ────────────────────────────────────────────────
export function FeedbackDisplay({
  question,
  wasCorrect,
  userAnswer,
  userAnswerIndex,
  motivationalText,
  xpGained,
  onNext,
  isLastQuestion,
  mode = 'full',
  streak = 0,
  levelUp,
  streakMilestone,
  dailyGoalReached,
  tierEvolution,
  skinUnlock,
  onClearLevelUp,
  moduleColor,
  familyMasteryPercent,
  familyLabel
}: FeedbackDisplayProps) {
  const {
    selectedVariant
  } = useDjaySelection();
  const [feedbackPhase, setFeedbackPhase] = useState<'splash' | 'detail'>('splash');
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [hasAutoExpanded, setHasAutoExpanded] = useState(false);
  // Reset phase when question changes
  useEffect(() => {
    if (mode === 'full') {
      setFeedbackPhase('splash');
      setExpandedSection(null);
      setHasAutoExpanded(false);
    }
  }, [question.id, mode]);
  // Keyboard: Enter → next (detail phase only)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter' && feedbackPhase === 'detail') onNext();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onNext, feedbackPhase]);
  // Auto-expand explanation for correct answers (wrong answers show explanation directly)
  useEffect(() => {
    if (wasCorrect && mode === 'full' && feedbackPhase === 'detail' && !hasAutoExpanded) {
      const timer = setTimeout(() => {
        setExpandedSection('explanation');
        setHasAutoExpanded(true);
      }, 600);
      return () => clearTimeout(timer);
    }
  }, [wasCorrect, mode, hasAutoExpanded, feedbackPhase]);
  // ── Derived data ───────────────────────────────────────────
  const isMoneyAssembler = question.questionType === 'money';
  const isTypedAnswerType = ['clock', 'fill_in', 'number_sequence', 'matching', 'ordering'].includes(question.questionType || '');
  const resolvedSupport = useMemo(() => resolveSupportContent(question), [question]);
  const correctAnswer = useMemo(() => formatCorrectAnswer(question), [question]);
  const displayUserAnswer = useMemo(() => formatUserAnswer(question, userAnswer), [question, userAnswer]);
  const userMoneyTokens = useMemo(() => isMoneyAssembler ? parseMoneyTokens(userAnswer) : null, [isMoneyAssembler, userAnswer]);
  const correctCents = useMemo(() => isMoneyAssembler ? normalizeCents(question.correctAnswer) : null, [isMoneyAssembler, question.correctAnswer]);
  const distractorExplanation = !wasCorrect && userAnswerIndex !== undefined && !isTypedAnswerType ? question.feedback?.distractorExplanations?.[userAnswerIndex.toString()] || 'Helaas, dat was niet het juiste antwoord. Kijk goed naar de uitleg hieronder om te zien hoe het wel moet!' : undefined;
  const accordionSections = useMemo(() => buildAccordionSections({
    question,
    wasCorrect,
    resolvedSupport,
    distractorExplanation,
    selectedVariant,
    levelUp,
    userMoneyTokens,
    correctCents,
    userAnswer
  }), [question, wasCorrect, resolvedSupport, distractorExplanation, selectedVariant, levelUp, userMoneyTokens, correctCents, userAnswer]);
  // ── COMPACT MODE ───────────────────────────────────────────
  if (mode === 'compact') {
    return <div className="w-full flex flex-col gap-4" data-id="element-9751">
        <StatusBadge isCorrect={wasCorrect} motivationalText={motivationalText} xpGained={xpGained} mode="compact" data-id="element-9752" />
        <AnswerComparison userAnswer={displayUserAnswer} correctAnswer={correctAnswer} isCorrect={wasCorrect} mode="compact" data-id="element-9753" />
        <ExplanationCard explanation={resolvedSupport.explanation || ''} distractorExplanation={distractorExplanation} isCorrect={wasCorrect} mode="compact" solutionSteps={resolvedSupport.solutionSteps} data-id="element-9754" />
        <div className="flex justify-end pt-2" data-id="element-9755">
          <Button onClick={onNext} variant={wasCorrect ? 'success' : 'primary'} size="sm" data-id="element-9756">
            {isLastQuestion ? 'Klaar' : 'Volgende'}
          </Button>
        </div>
      </div>;
  }
  // ── SPLASH PHASE ───────────────────────────────────────────
  if (feedbackPhase === 'splash') {
    return <AnimatePresence mode="wait" data-id="element-9757">
        <FeedbackSplash isCorrect={wasCorrect} xpGained={xpGained} streak={streak} levelUp={levelUp} streakMilestone={streakMilestone} dailyGoalReached={dailyGoalReached} tierEvolution={tierEvolution} skinUnlock={skinUnlock} moduleColor={moduleColor} familyMasteryPercent={familyMasteryPercent} familyLabel={familyLabel} onComplete={() => setFeedbackPhase('detail')} data-id="element-9758" />
      </AnimatePresence>;
  }
  // ── DETAIL PHASE ───────────────────────────────────────────
  return <motion.div role="status" aria-live="polite" aria-atomic="true" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} transition={{
    duration: 0.3
  }} className="w-full max-w-2xl mx-auto flex flex-col min-h-[calc(100vh-140px)]" data-id="element-9759">
      {/* 1. Answer Comparison */}
      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.4
    }} className="mb-5 px-4" data-id="element-9760">
        <AnswerComparison userAnswer={displayUserAnswer} correctAnswer={correctAnswer} isCorrect={wasCorrect} mode="inline" data-id="element-9761" />
      </motion.div>

      {/* 2. Question Context (minimal) */}
      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.1,
      duration: 0.4
    }} className="mb-5 px-4" data-id="element-9762">
        <div className="text-center" data-id="element-9763">
          <p className="text-[10px] font-black uppercase text-slate-400 mb-1.5 tracking-wider" data-id="element-9764">
            De vraag was
          </p>
          <div className="text-sm font-medium text-slate-600" data-id="element-9765">
            <LatexRenderer content={question.question} data-id="element-9766" />
          </div>
        </div>
      </motion.div>

      {/* 3. Explanation Section — always visible for wrong answers */}
      {!wasCorrect ? <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.2,
      duration: 0.4
    }} className="mb-6 px-4" data-id="element-9767">
          <div className="bg-white rounded-2xl border-l-4 border-orange-400 shadow-sm p-5" data-id="element-9768">
            <ExplanationContent explanation={resolvedSupport.explanation || ''} distractorExplanation={distractorExplanation} isCorrect={wasCorrect} solutionSteps={resolvedSupport.solutionSteps} data-id="element-9769" />
          </div>
        </motion.div> : null}

      {/* 4. Extra Sections (accordion) */}
      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.3,
      duration: 0.4
    }} className="flex-1 mb-6 px-4" data-id="element-9770">
        <FeedbackAccordion sections={accordionSections} expandedSection={expandedSection} onToggle={setExpandedSection} data-id="element-9771" />
      </motion.div>

      {/* Next Button */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.4,
      duration: 0.4,
      type: 'spring',
      stiffness: 300,
      damping: 25
    }} className="sticky bottom-4 z-20 mt-auto px-4" data-id="element-9772">
        <Button size="xxl" onClick={onNext} variant={wasCorrect ? 'success' : 'primary'} className="w-full shadow-xl hover:shadow-2xl transition-all hover:-translate-y-1 group rounded-2xl" data-id="element-9773">
          {wasCorrect ? 'Volgende vraag' : 'Ik snap het, volgende!'}
          <motion.span animate={{
          x: [0, 4, 0]
        }} transition={{
          duration: 1.5,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: 1
        }} className="ml-3 inline-flex" data-id="element-9774">
            <ArrowRight className="w-6 h-6" strokeWidth={3} data-id="element-9775" />
          </motion.span>
        </Button>
      </motion.div>
    </motion.div>;
}